/**
 * SIP Configuration Page
 * 
 * Manage SIP peers using shared SipPeerInfo types.
 * Refactored to use common components for consistent UI patterns.
 */

import React, { useState, useMemo } from 'react';
import { 
  Phone,
  Plus, 
  CheckCircle, 
  Globe,
  Wifi,
  Monitor,
  Building2,
  XCircle,
  Shield,
  Heart,
  Unlock,
  KeyRound,
  Ban,
  Music,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import type { SipPeerInfo, SipPeersResponse } from '../../types/sip';
import { SipPeerEditor } from '../../components/sip/sip-peer-editor/SipPeerEditor';
import { TemplatePicker } from '../../components/sip/sip-peer-editor/TemplatePicker';
import type { TemplateKey } from '../../components/sip/sip-peer-editor/schema';

// Common components
import {
  PageHeader,
  PageContainer,
  StatsGrid,
  StatCard,
  FilterBar,
  ItemCard,
  ItemCardIcon,
  ItemActions,
  EmptyState,
  LoadingState,
  ErrorState,
  DeleteConfirmModal,
  TransportBadge,
  type FilterOption,
} from '../../components/common';
import { useViewMode } from '../../hooks/useViewMode';

// Use the shared SipPeerInfo type
type SipPeer = SipPeerInfo;

// Extended type for new fields
interface ExtendedPeerFields {
  codecGroupId?: string | null;
  registrationPolicy?: 'open' | 'authenticated' | 'closed';
  outboundTlsConfig?: {
    certificateId?: string;
    verifyServer?: boolean;
    minVersion?: string;
    sni?: string;
  } | null;
  routingConfig?: {
    optionsPingEnabled?: boolean;
    optionsPingInterval?: number;
    endpoints?: unknown[];
    endpointSelectionMethod?: string;
  } | null;
}

type ExtendedPeer = SipPeer & ExtendedPeerFields;

// ========================================
// CONSTANTS
// ========================================

type FilterType = 'all' | 'enabled' | 'disabled' | 'teams' | 'carrier' | 'webrtc' | 'softphone' | 'pbx';

const FILTER_OPTIONS: FilterOption[] = [
  { value: 'all', label: 'All Peers' },
  { value: 'enabled', label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' },
  { value: 'teams', label: 'Teams' },
  { value: 'carrier', label: 'Carrier' },
  { value: 'webrtc', label: 'WebRTC' },
  { value: 'softphone', label: 'Softphone' },
  { value: 'pbx', label: 'PBX' },
];

const PEER_TYPE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  teams: Building2,
  carrier: Phone,
  webrtc: Wifi,
  softphone: Monitor,
  pbx: Globe,
};

const PEER_TYPE_LABELS: Record<string, string> = {
  teams: 'Microsoft Teams',
  carrier: 'PSTN Carrier',
  webrtc: 'WebRTC',
  softphone: 'SIP Softphone',
  pbx: 'PBX System',
};

// ========================================
// MAIN COMPONENT
// ========================================

export function SipConfigurationPage() {
  const queryClient = useQueryClient();
  
  // State management - use user preference for default view mode
  const [viewMode, setViewMode] = useViewMode();
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [templateKey, setTemplateKey] = useState<TemplateKey | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [peerToDelete, setPeerToDelete] = useState<SipPeer | null>(null);
  const [editingPeer, setEditingPeer] = useState<SipPeer | null>(null);

  // Create flow: pick a template first, then open the editor seeded with it.
  const handleAddPeer = () => {
    setEditingPeer(null);
    setTemplateKey(null);
    setShowTemplatePicker(true);
  };
  const handleTemplateSelected = (key: TemplateKey | null) => {
    setTemplateKey(key);
    setEditingPeer(null);
    setShowTemplatePicker(false);
    setShowEditor(true);
  };
  const handleEditPeer = (peer: SipPeer) => {
    setEditingPeer(peer);
    setTemplateKey(null);
    setShowEditor(true);
  };
  const handleEditorClose = () => {
    setShowEditor(false);
    setEditingPeer(null);
    setTemplateKey(null);
  };

  // API queries
  const peersQuery = useQuery<SipPeersResponse>({
    queryKey: ['sip-peers'],
    queryFn: async () => {
      const response = await api.get<{ 
        success: boolean; 
        data: SipPeer[];
      }>('/api/sip-peers');
      return response.data;
    }
  });
  
  const peers = peersQuery.data?.data ?? [];

  // Fetch codec groups for name lookup
  const codecGroupsQuery = useQuery({
    queryKey: ['codec-groups'],
    queryFn: async () => {
      const response = await api.get<{ 
        success: boolean; 
        data: { id: string; name: string }[];
      }>('/api/codec-groups');
      return response.data.success ? response.data.data : [];
    }
  });
  
  const codecGroupsMap = useMemo(() => {
    const map = new Map<string, string>();
    if (codecGroupsQuery.data) {
      codecGroupsQuery.data.forEach((group) => {
        map.set(group.id, group.name);
      });
    }
    return map;
  }, [codecGroupsQuery.data]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (peerId: string) => {
      await api.delete(`/api/sip-peers/${peerId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sip-peers'] });
      setShowDeleteModal(false);
      setPeerToDelete(null);
    },
    onError: (error: Error) => {
      console.error('Failed to delete SIP peer:', error);
      alert('Failed to delete SIP peer. Please try again.');
    }
  });

  // Toggle enabled mutation
  const toggleEnabledMutation = useMutation({
    mutationFn: async ({ peerId, enabled }: { peerId: string; enabled: boolean }) => {
      await api.patch(`/api/sip-peers/${peerId}`, { enabled });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sip-peers'] });
    }
  });

  // Utility functions
  const getStatusColor = (enabled: boolean) => {
    return enabled 
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Filter and search logic
  const filteredPeers = peers.filter(peer => {
    let matchesType = false;
    if (filterType === 'all') {
      matchesType = true;
    } else if (filterType === 'enabled') {
      matchesType = peer.enabled === true;
    } else if (filterType === 'disabled') {
      matchesType = peer.enabled === false;
    } else {
      matchesType = peer.type === filterType;
    }

    const matchesSearch = searchQuery === '' || 
      peer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      peer.domain?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      peer.sipUri?.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesType && matchesSearch;
  });

  const sortedPeers = [...filteredPeers].sort((a, b) => {
    return a.name.localeCompare(b.name);
  });

  // Calculate statistics
  const stats = {
    total: peers.length,
    enabled: peers.filter(peer => peer.enabled === true).length,
    disabled: peers.filter(peer => peer.enabled === false).length,
    carriers: peers.filter(peer => peer.type === 'carrier').length,
  };

  // Loading state
  if (peersQuery.isLoading) {
    return <LoadingState />;
  }

  // Error state
  if (peersQuery.error) {
    return (
      <ErrorState
        title="Failed to load SIP peers"
        message={peersQuery.error instanceof Error ? peersQuery.error.message : 'Unknown error'}
        onRetry={() => peersQuery.refetch()}
      />
    );
  }

  return (
    <PageContainer>
      {/* Header */}
      <PageHeader
        title="SIP Peers"
        description="Manage SIP peer connections and carrier configurations"
        onRefresh={() => peersQuery.refetch()}
        isRefreshing={peersQuery.isFetching}
        primaryAction={{
          label: 'Add Peer',
          icon: Plus,
          onClick: handleAddPeer,
        }}
      />

      {/* Statistics Cards */}
      <StatsGrid>
        <StatCard
          label="Total Peers"
          value={stats.total}
          icon={Globe}
          iconBgColor="bg-blue-100"
          iconColor="text-blue-600"
        />
        <StatCard
          label="Enabled"
          value={stats.enabled}
          icon={CheckCircle}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
          valueColor="text-green-600"
        />
        <StatCard
          label="Disabled"
          value={stats.disabled}
          icon={XCircle}
          iconBgColor="bg-gray-100"
          iconColor="text-gray-600"
          valueColor="text-gray-600"
        />
        <StatCard
          label="Carriers"
          value={stats.carriers}
          icon={Phone}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
          valueColor="text-green-600"
        />
      </StatsGrid>

      {/* Filters and Search */}
      <FilterBar
        searchPlaceholder="Search peers..."
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        filterOptions={FILTER_OPTIONS}
        filterValue={filterType}
        onFilterChange={(value) => setFilterType(value as FilterType)}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {/* Peers Display */}
      {sortedPeers.length === 0 ? (
        <EmptyState
          icon={Globe}
          title="No SIP peers found"
          description="Get started by adding a SIP peer."
          isFiltered={searchQuery !== '' || filterType !== 'all'}
          action={{
            label: 'Add SIP Peer',
            icon: Plus,
            onClick: handleAddPeer,
          }}
        />
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {sortedPeers.map((peer) => {
            const extPeer = peer as ExtendedPeer;
            const TypeIcon = PEER_TYPE_ICONS[peer.type] || Globe;

            return (
              <ItemCard
                key={peer.id}
                icon={
                  <ItemCardIcon 
                    icon={<TypeIcon className={`w-5 h-5 ${peer.enabled ? 'text-blue-600' : 'text-gray-400'}`} />} 
                    bgColor={peer.enabled ? 'bg-blue-100' : 'bg-gray-100'} 
                  />
                }
                title={peer.name}
                subtitle={PEER_TYPE_LABELS[peer.type] || peer.type}
                status={
                  <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(peer.enabled)}`}>
                    {peer.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                }
                details={[
                  { label: 'Domain', value: <span className="font-mono text-xs">{peer.domain || '-'}</span> },
                  { label: 'Transport', value: <TransportBadge transport={peer.transport} size="sm" /> },
                ]}
                tags={
                  <div className="flex flex-wrap gap-1">
                    {/* Registration Policy */}
                    {(() => {
                      const policy = extPeer.registrationPolicy || 'open';
                      const config = {
                        open: { icon: Unlock, color: 'bg-green-50 text-green-700', label: 'Open' },
                        authenticated: { icon: KeyRound, color: 'bg-blue-50 text-blue-700', label: 'Auth' },
                        closed: { icon: Ban, color: 'bg-red-50 text-red-700', label: 'Closed' }
                      }[policy];
                      const PolicyIcon = config.icon;
                      return (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
                          <PolicyIcon className="w-3 h-3 mr-1" />
                          {config.label}
                        </span>
                      );
                    })()}
                    
                    {/* TLS indicator */}
                    {peer.transport === 'TLS' && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700">
                        <Shield className="w-3 h-3 mr-1" />
                        TLS
                      </span>
                    )}
                    
                    {/* Codec Group */}
                    {extPeer.codecGroupId && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700">
                        <Music className="w-3 h-3 mr-1" />
                        {codecGroupsMap.get(extPeer.codecGroupId) || 'Codec'}
                      </span>
                    )}
                    
                    {/* OPTIONS Ping */}
                    {extPeer.routingConfig?.optionsPingEnabled && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                        <Heart className="w-3 h-3 mr-1" />
                        Health
                      </span>
                    )}
                  </div>
                }
                actions={
                  <ItemActions
                    variant="card"
                    actions={[
                      { kind: 'edit', onClick: () => handleEditPeer(peer) },
                      {
                        kind: peer.enabled ? 'disable' : 'enable',
                        onClick: () => toggleEnabledMutation.mutate({ peerId: peer.id, enabled: !peer.enabled }),
                        disabled: toggleEnabledMutation.isPending,
                      },
                      {
                        kind: 'delete',
                        onClick: () => {
                          setPeerToDelete(peer);
                          setShowDeleteModal(true);
                        },
                      },
                    ]}
                  />
                }
              />
            );
          })}
        </div>
      ) : (
        // List View
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Peer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Domain</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transport</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedPeers.map((peer) => {
                return (
                  <tr key={peer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <div className="text-sm font-medium text-gray-900">{peer.name}</div>
                        {peer.sipUri && (
                          <div className="text-xs text-gray-500 font-mono">{peer.sipUri}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                        {PEER_TYPE_LABELS[peer.type] || peer.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {peer.domain || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <TransportBadge transport={peer.transport} size="sm" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(peer.enabled)}`}>
                        {peer.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <ItemActions
                        variant="table"
                        actions={[
                          { kind: 'edit', onClick: () => handleEditPeer(peer) },
                          {
                            kind: peer.enabled ? 'disable' : 'enable',
                            onClick: () => toggleEnabledMutation.mutate({ peerId: peer.id, enabled: !peer.enabled }),
                            disabled: toggleEnabledMutation.isPending,
                          },
                          {
                            kind: 'delete',
                            onClick: () => {
                              setPeerToDelete(peer);
                              setShowDeleteModal(true);
                            },
                          },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create flow: template picker → editor */}
      <TemplatePicker
        isOpen={showTemplatePicker}
        onClose={() => setShowTemplatePicker(false)}
        onSelect={handleTemplateSelected}
      />

      {/* SIP Peer Editor (tabbed modal) */}
      <SipPeerEditor
        isOpen={showEditor}
        onClose={handleEditorClose}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['sip-peers'] });
        }}
        editingPeer={editingPeer}
        templateKey={templateKey}
      />

      {/* Delete Confirmation Modal */}
      {peerToDelete && (
        <DeleteConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setPeerToDelete(null);
          }}
          onConfirm={() => deleteMutation.mutate(peerToDelete.id)}
          title="Delete SIP Peer"
          entityType="peer"
          entityName={peerToDelete.name}
          additionalInfo={
            <>
              <p className="text-sm text-gray-600 mb-1 mt-2">Type:</p>
              <p className="text-sm text-gray-700">
                {PEER_TYPE_LABELS[peerToDelete.type] || peerToDelete.type}
              </p>
            </>
          }
          isDeleting={deleteMutation.isPending}
        />
      )}
    </PageContainer>
  );
}

export default SipConfigurationPage;
